import React from 'react';

import type { SelectProps } from '@mui/material';
import type { FormikProps } from 'formik';

import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';

import { ReactComponent as CloseIcon } from 'assets/cross.icon.svg';

interface ISelectOptions {
  label: string;
  value: string | number;
}

interface IOverrideProps {
  name: string;
  label: string;
  defaultValue: string;
  multiple?: never;
  onChange?: never;
  onBlur?: never;
}

interface IProps extends Omit<SelectProps, keyof IOverrideProps>, IOverrideProps {
  formik: FormikProps<any>;
  options: ISelectOptions[];
}

const FormSelect: React.FC<IProps> = ({
  name,
  formik,
  label,
  fullWidth = true,
  defaultValue = '',
  options,
  size = 'small',
  ...selectProps
}) => {
  const labelId = `${name}-label`;
  const value = formik.values?.[name] ?? defaultValue;
  const hasError = Boolean(formik.touched?.[name] && formik.errors?.[name]);

  return (
    <FormControl fullWidth={fullWidth} error={hasError} variant="outlined" size={size}>
      <InputLabel id={labelId} htmlFor={name}>
        {label}
      </InputLabel>
      <Select
        {...selectProps}
        labelId={labelId}
        id={name}
        name={name}
        label={label}
        multiple={false}
        value={value}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        input={<OutlinedInput label={label} />}
        endAdornment={
          value ? (
            <CloseIcon
              style={{ cursor: 'pointer', marginRight: '15px', width: '20px', height: '20px' }}
              onClick={e => {
                e.stopPropagation();
                formik.setFieldValue(name, '');
              }}
            />
          ) : undefined
        }
      >
        {options.map(({ label: optionLabel, value: optionValue }) => (
          <MenuItem key={optionValue} value={optionValue}>
            {optionLabel}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default FormSelect;
